import React from "react"
import { Link } from "react-router-dom"
import Icon from "../components/Icon"

function Navigation() {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false)

  return (
    <nav className="w-full bg-white/95 backdrop-blur-sm border-b border-gray-200/50 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <img
              src="/logofinalrounded.png"
              alt="RecordLOGS logo"
              className="w-8 h-8 rounded-lg object-cover shadow-sm"
            />
            <span className="text-xl font-bold text-slate-800">RecordLOGS</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-slate-600 hover:text-green-600 transition-colors">
              Home
            </Link>
            <Link to="/available-units" className="text-slate-600 hover:text-green-600 transition-colors">
              Search Units
            </Link>
            <Link to="/login" className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors">
              Sign In
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors"
            aria-label="Toggle menu"
          >
            <Icon name={isMenuOpen ? "x" : "list"} size="1.25rem" />
          </button>
        </div>

        {/* Mobile Navigation Menu */}
        {isMenuOpen && (
          <div className="md:hidden border-t border-gray-200 py-4">
            <div className="flex flex-col space-y-3">
              <Link 
                to="/" 
                className="px-3 py-2 text-slate-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                <Icon name="house" className="inline mr-2" size="1rem" />
                Home
              </Link>
              <Link 
                to="/available-units" 
                className="px-3 py-2 text-slate-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                <Icon name="search" className="inline mr-2" size="1rem" />
                Search Units
              </Link>
              <Link 
                to="/privacy-policy" 
                className="px-3 py-2 text-slate-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                <Icon name="shield-lock" className="inline mr-2" size="1rem" />
                Privacy Policy
              </Link>
              <Link 
                to="/login" 
                className="px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-center font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                <Icon name="box-arrow-in-right" className="inline mr-2" size="1rem" />
                Sign In
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  )
}

function HeroSection() {
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-green-50">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(34,197,94,0.1),transparent_50%),radial-gradient(circle_at_80%_80%,rgba(34,197,94,0.1),transparent_50%)]" />
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
        <div className="text-center">
          <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Icon name="book" size="1.5rem" className="text-green-600" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-800 leading-tight mb-4">
            User Guide
          </h1>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto mb-8">
            Master RecordLOGS with our comprehensive guide. Learn how to manage properties, process payments, and streamline your rental operations.
          </p>
        </div>
      </div>
    </section>
  )
}

function GuideOverview() {
  return (
    <section className="py-12 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-slate-800 mb-4">RecordLOGS (RL) Quick User Guide</h2>
        </div>
      </div>
    </section>
  )
}

function GuideContent() {
  const sections = [
    {
      title: "1. Getting Started",
      icon: "play-circle",
      items: [
        "Sign up with Name, Phone/Email, ID, Password.",
        "Verify with OTP → Login.",
        "One account works across all modules."
      ]
    },
    {
      title: "2. Wallet",
      icon: "wallet2",
      items: [
        "Top Up: Mpesa Paybill 4123905, Account = User ID.",
        "Payments: Autopay or click Pay button."
      ]
    },
    {
      title: "3. Modules",
      icon: "grid-3x3-gap",
      items: [
        "FRP (For Rent Portal): Tenants pay rent & get receipts. Landlords/managers track collections.",
        "Groups: Manage membership, contributions, and reports.",
        "School: Attendance, report cards, fees, and other school records.",
        "Vehicle: Logs for fuel, maintenance, drivers."
      ]
    },
    {
      title: "4. Roles",
      icon: "people",
      items: [
        "Admin: Full control.",
        "Manager: Runs a module.",
        "Members/Users: Tenants, students, drivers, or group participants."
      ]
    },
    {
      title: "5. Security",
      icon: "shield-check",
      items: [
        "Use 2FA + strong password.",
        "All payments are encrypted."
      ]
    },
    {
      title: "6. Support",
      icon: "question-circle",
      items: [
        "In-app Help Center & Chat.",
        "Email: support@recordlogs.com"
      ]
    }
  ]

  return (
    <section className="py-16 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="space-y-12">
          {sections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="bg-white rounded-2xl p-8 shadow-sm border border-slate-200">
              <div className="flex items-center gap-4 mb-8">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <Icon name={section.icon} size="1.25rem" className="text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-slate-800">{section.title}</h3>
              </div>

              <div className="space-y-4">
                {section.items.map((item, itemIndex) => (
                  <div key={itemIndex} className="flex items-start gap-4">
                    <div className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center font-semibold text-xs mt-1">
                      •
                    </div>
                    <p className="text-slate-600 leading-relaxed flex-1">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}


function Footer() {
  return (
    <footer className="bg-slate-800 text-white py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <img
                src="/logofinalrounded.png"
                alt="RecordLOGS logo"
                className="w-8 h-8 rounded-lg object-cover shadow-sm"
              />
              <span className="text-xl font-bold">RecordLOGS</span>
            </div>
            <p className="text-slate-300 mb-4 max-w-md">
              Need more help? Contact our support team or explore additional resources to master RecordLOGS.
            </p>
          </div>
        </div>
        
        <div className="border-t border-slate-700 mt-8 pt-8 text-center text-slate-400">
          <p>© {new Date().getFullYear()} RecordLOGS. All rights reserved.</p>
        </div>
      </div>
    </footer>
  )
}

function UserGuidePage() {
  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      <main>
        <HeroSection />
        <GuideOverview />
        <GuideContent />
      </main>
      <Footer />
    </div>
  )
}

export default UserGuidePage
